import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import brandLogo from '../assets/devgenius-logo.svg';

export default function Header({ type, toggleSidebar, toggleNotification }) {
  const { logout } = useAuth();
  const { user } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  // Notification count state
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Track scroll for header blur effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch notifications count for the logged-in user
  useEffect(() => {
    if (user?.id) {
      fetch(`http://localhost:5000/notifications/${user.id}`)
        .then(res => res.json())
        .then(data => {
          setNotificationCount(Array.isArray(data) ? data.length : 0);
        })
        .catch(() => setNotificationCount(0));
    }
  }, [user]);

  // Handle notification icon click
  const handleNotificationClick = () => {
    setNotificationsOpen(true);
    if (toggleNotification) toggleNotification();
  };

  // Hide badge if notifications are open
  const showBadge = notificationCount > 0 && !notificationsOpen;

  const handleLogout = () => {
    logout();
    navigate('/Login');
  };

  // Determine accent gradient based on role
  const getRoleAccent = () => {
    if (user?.role === 'admin') return 'linear-gradient(135deg, #6366f1, #8b5cf6)';
    if (user?.role === 'professional') return 'linear-gradient(135deg, #10b981, #059669)';
    if (user?.role === 'client') return 'linear-gradient(135deg, #f59e0b, #ec4899)';
    return 'linear-gradient(135deg, #00d4ff, #7c3aed)';
  };

  const LanguageSwitch = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <button
        type="button"
        onClick={() => setLanguage('en')}
        style={{
          padding: '4px 10px',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: 600,
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          background: language === 'en' ? 'var(--gradient-primary)' : 'var(--bg-glass)',
          color: language === 'en' ? '#fff' : 'var(--text-secondary)',
          backdropFilter: 'blur(8px)',
        }}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => setLanguage('ar')}
        style={{
          padding: '4px 10px',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: 600,
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          background: language === 'ar' ? 'var(--gradient-primary)' : 'var(--bg-glass)',
          color: language === 'ar' ? '#fff' : 'var(--text-secondary)',
          backdropFilter: 'blur(8px)',
        }}
      >
        AR
      </button>
    </div>
  );

  // Logo component
  const Logo = () => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
    >
      <img
        src={brandLogo}
        alt="DevGenius logo"
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          boxShadow: '0 4px 15px rgba(var(--accent-primary-rgb), 0.3)',
        }}
      />
      <span style={{
        fontFamily: 'var(--font-display)',
        fontSize: '1.5rem',
        fontWeight: 800,
        background: getRoleAccent(),
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        letterSpacing: '-0.03em',
      }}>
        DevGenius
      </span>
    </motion.div>
  );

  // Nav link component
  const NavLink = ({ to, children, delay = 0 }) => (
    <motion.li
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: [0.16, 1, 0.3, 1] }}
      style={{ listStyle: 'none' }}
    >
      <Link
        to={to}
        style={{
          color: 'var(--text-secondary)',
          fontSize: '0.9rem',
          fontWeight: 500,
          padding: '6px 14px',
          borderRadius: '9999px',
          transition: 'all 0.3s ease',
          textDecoration: 'none',
          display: 'inline-block',
        }}
        onMouseEnter={e => {
          e.target.style.color = 'var(--text-primary)';
          e.target.style.background = 'var(--bg-glass)';
        }}
        onMouseLeave={e => {
          e.target.style.color = 'var(--text-secondary)';
          e.target.style.background = 'transparent';
        }}
      >
        {children}
      </Link>
    </motion.li>
  );

  // Shared header style
  const headerStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 50,
    padding: '16px 0',
    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    background: scrolled ? 'rgba(var(--bg-nav-rgb), 0.85)' : 'rgba(var(--bg-nav-rgb), 0.5)',
    backdropFilter: scrolled ? 'blur(20px) saturate(1.3)' : 'blur(12px)',
    WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(1.3)' : 'blur(12px)',
    borderBottom: scrolled ? '1px solid var(--border-glass)' : '1px solid transparent',
  };

  // Public headers (main, BusSignUp, proSignUp)
  if (type === 'main' || type === 'BusSignUp' || type === 'proSignUp') {
    const links = type === 'BusSignUp' 
      ? [{ to: '/', label: t('home') }, { to: '/Login', label: t('login') }, { to: '/ProSign-up', label: t('signupProfessional') }]
      : type === 'proSignUp'
      ? [{ to: '/', label: t('home') }, { to: '/Login', label: t('login') }, { to: '/BSign-up', label: t('signupBusiness') }]
      : [{ to: '/', label: t('home') }, { to: '/Login', label: t('login') }, { to: '/BSign-up', label: t('signupBusiness') }];

    return (
      <header style={headerStyle}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Logo />
          </Link>
          <nav>
            <ul style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              margin: 0,
              padding: 0,
            }}>
              {links.map((link, i) => (
                <NavLink key={link.to} to={link.to} delay={0.1 + i * 0.05}>
                  {link.label}
                </NavLink>
              ))}
              <motion.li
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                style={{ listStyle: 'none', marginLeft: '8px' }}
              >
                <LanguageSwitch />
              </motion.li>
            </ul>
          </nav>
        </div>
      </header>
    );
  }

  // Authenticated header
  return (
    <header style={headerStyle}>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '0 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        {/* Sidebar toggle */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={toggleSidebar}
          style={{
            position: 'fixed',
            top: '18px',
            left: '20px',
            zIndex: 60,
            background: getRoleAccent(),
            border: 'none',
            borderRadius: '12px',
            padding: '10px',
            cursor: 'pointer',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(var(--accent-primary-rgb), 0.3)',
          }}
          aria-label="Toggle Sidebar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </motion.button>

        {/* Logo */}
        <div style={{ marginLeft: '60px' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Logo />
          </Link>
        </div>

        {/* Right section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
        >
          <LanguageSwitch />
          
          {/* Notification bell */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleNotificationClick}
            style={{
              position: 'relative',
              background: 'var(--bg-glass)',
              border: '1px solid var(--border-glass)',
              borderRadius: '12px',
              padding: '8px',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--border-accent)';
              e.currentTarget.style.color = 'var(--accent-cyan)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border-glass)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
            aria-label="Notifications"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <AnimatePresence>
              {showBadge && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: 'linear-gradient(135deg, #f43f5e, #e11d48)',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    boxShadow: '0 2px 8px rgba(244, 63, 94, 0.4)',
                  }}
                >
                  {notificationCount}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>

          {/* Logout */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleLogout}
            style={{
              background: 'var(--bg-glass)',
              border: '1px solid var(--border-glass)',
              borderRadius: '9999px',
              padding: '8px 20px',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              fontSize: '0.85rem',
              fontWeight: 600,
              fontFamily: 'var(--font-body)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'rgba(244, 63, 94, 0.3)';
              e.currentTarget.style.color = '#f43f5e';
              e.currentTarget.style.background = 'rgba(244, 63, 94, 0.1)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border-glass)';
              e.currentTarget.style.color = 'var(--text-secondary)';
              e.currentTarget.style.background = 'var(--bg-glass)';
            }}
          >
            {t('logout')}
          </motion.button>
        </motion.div>
      </div>
    </header>
  );
}



